import React, { useState, useRef } from 'react';
import DemoBanner from '../components/DemoBanner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';

const { FiArrowRight, FiActivity, FiUpload, FiX, FiShield, FiUserCheck, FiInfo, FiAlertCircle } = FiIcons;

const AIHeadhunter = () => {
  const { t } = useLanguage();
  const [cvText, setCvText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFileName(file.name);
      // Local extraction simulation based on filename for demo
      if (file.name.toLowerCase().includes('onderwijs') || file.name.toLowerCase().includes('docent')) {
        setCvText("Docent Wiskunde. 10 jaar ervaring in het voortgezet onderwijs. Curriculum ontwikkeling en didactiek.");
      } else if (file.name.toLowerCase().includes('it')) {
        setCvText("Senior Developer. React, Node.js, AWS.");
      }
    }
  };

  const clearFile = () => {
    setUploadedFileName('');
    setCvText('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAnalyze = () => {
    if (!cvText.trim() && !uploadedFileName) return;
    setIsAnalyzing(true);
    setShowResults(false);

    const input = cvText.toLowerCase();

    // Domain Detection Logic with Priority
    const domains = [
      {
        name: "Education",
        title: "Teacher / Education Advisor",
        keywords: ['docent', 'leraar', 'onderwijs', 'teacher', 'school', 'curriculum', 'pedagogisch', 'education', 'student', 'didactics', 'pedagogy', 'classroom', 'teaching', 'education advisor', 'school leadership', 'mentor', 'lecturer', 'trainer', 'opleiding', 'docentschap'],
        skills: ["Didactiek", "Klassenmanagement", "Curriculum Ontwikkeling"],
        matches: [
          { title: "Senior Onderwijsadviseur", explanation: "Uw uitgebreide ervaring in didactiek sluit aan bij strategische onderwijsrollen." },
          { title: "Teamleider Onderwijs", explanation: "Ervaring in schoolmanagement suggereert een stap naar leidinggeven." }
        ]
      },
      {
        name: "Healthcare",
        title: "Healthcare Professional",
        keywords: ['verpleegkundige', 'zorg', 'patiënt', 'ziekenhuis', 'medical', 'healthcare', 'doctor', 'clinic', 'nursing'],
        skills: ["Patiëntenzorg", "Klinisch redeneren", "Multidisciplinair overleg"],
        matches: [
          { title: "Verpleegkundig Specialist", explanation: "Harde zorgskills matchen met gespecialiseerde klinische rollen." }
        ]
      },
      {
        name: "Legal",
        title: "Legal Expert",
        keywords: ['advocaat', 'jurist', 'legal', 'law', 'attorney', 'recht', 'wetgeving', 'lawyer'],
        skills: ["Juridische analyse", "Contractbeheer", "Wet- en regelgeving"],
        matches: [
          { title: "Legal Counsel", explanation: "Expertise in wetgeving is direct overdraagbaar naar bedrijfsjuridische rollen." }
        ]
      },
      {
        name: "Finance",
        title: "Finance Specialist",
        keywords: ['finance', 'accountant', 'boekhouding', 'bank', 'fiscaal', 'belasting', 'financial', 'audit'],
        skills: ["Financiële rapportage", "Auditing", "Risk Management"],
        matches: [
          { title: "Financial Controller", explanation: "Analytisch vermogen in cijfers past bij sturende financiële functies." }
        ]
      },
      {
        name: "Management",
        title: "Managerial Leader",
        keywords: ['manager', 'directeur', 'lead', 'head of', 'operations', 'management', 'director', 'executive'],
        skills: ["Strategische planning", "Stakeholder Management", "Operationele efficiëntie"],
        matches: [
          { title: "Operations Manager", explanation: "Bewezen leiderschap op strategisch niveau matcht met directieve rollen." }
        ]
      },
      {
        name: "Marketing",
        title: "Marketing professional",
        keywords: ['marketing', 'seo', 'sea', 'branding', 'content', 'social media', 'advertising'],
        skills: ["Content Strategie", "Growth Marketing", "Brand Identity"],
        matches: [
          { title: "Marketing Director", explanation: "Creativiteit en data-gedreven aanpak passen bij leidinggevende marketingrollen." }
        ]
      },
      {
        name: "Sales",
        title: "Sales Specialist",
        keywords: ['sales', 'verkoop', 'account manager', 'bdr', 'targets', 'business development'],
        skills: ["Relatiebeheer", "Onderhandelen", "Pipeline management"],
        matches: [
          { title: "Key Account Manager", explanation: "Commerciële drive en resultaatgerichtheid matchen met senior sales." }
        ]
      },
      {
        name: "Government / Policy",
        title: "Policy Advisor",
        keywords: ['overheid', 'beleid', 'gemeente', 'ambtenaar', 'policy', 'public sector', 'civil servant'],
        skills: ["Bestuurskunde", "Beleidsontwikkeling", "Maatschappelijke impact"],
        matches: [
          { title: "Beleidsmedewerker Overheid", explanation: "Ervaring met publieke belangen en wetgeving is cruciaal voor deze rol." }
        ]
      },
      {
        name: "Research / Academia",
        title: "Academic Researcher",
        keywords: ['research', 'researcher', 'phd', 'professor', 'scientist', 'onderzoek', 'academia'],
        skills: ["Methodologie", "Data-analyse", "Publiceren"],
        matches: [
          { title: "University Lecturer", explanation: "Academische verdieping en onderzoekservaring passen bij docentschap in het HO." }
        ]
      },
      {
        name: "IT / Technology",
        title: "Tech Specialist",
        keywords: ['developer', 'software', 'engineer', 'react', 'node', 'cloud', 'aws', 'technology', 'it', 'programming', 'coder'],
        skills: ["Fullstack Development", "Cloud Architecture", "System Design"],
        matches: [
          { title: "Senior Software Engineer", explanation: "Uw ervaring met moderne tech-stacks matcht met product-engineering teams." }
        ]
      }
    ];

    // Priority Check
    let detected = domains.find(d => d.keywords.some(kw => input.includes(kw)));

    // Fallback Result
    const fallbackResult = {
      domain: "General Professional",
      skills: ["Communicatie", "Projectbeheer", "Analytisch vermogen"],
      matches: [
        { title: "General Manager", explanation: "Uw veelzijdige profiel suggereert een brede inzetbaarheid in diverse sectoren." }
      ]
    };

    setTimeout(() => {
      setResult(detected ? {
        domain: detected.name,
        skills: detected.skills,
        matches: detected.matches
      } : fallbackResult);
      
      setIsAnalyzing(false);
      setShowResults(true);
    }, 1500);
  };

  return (
    <div className="min-h-screen py-20 bg-gray-50/30 font-inter">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <DemoBanner />
        
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-white text-brand-orange rounded-full text-[10px] font-black uppercase tracking-[0.3em] mb-8 shadow-sm border border-orange-50">
            <SafeIcon icon={FiShield} /> {t.nav.headhunter}
          </div>
          <h1 className="text-5xl font-black text-brand-dark mb-6 tracking-tighter italic uppercase">{t.headhunter.title}</h1>
          <p className="text-lg text-gray-500 italic font-medium">{t.headhunter.subtitle}</p>
        </div>

        <div className="bg-white rounded-[2.5rem] border border-gray-100 p-10 shadow-sm mb-16">
          <div className="p-8 bg-orange-50/30 border border-orange-100 rounded-2xl relative mb-8">
            <label className="block text-[10px] font-black text-brand-orange mb-6 flex items-center gap-3 uppercase tracking-[0.2em]">
              <SafeIcon icon={FiUpload} /> {t.headhunter.label}
            </label>
            
            {uploadedFileName ? (
              <div className="flex items-center justify-between p-5 bg-white border border-brand-orange/20 rounded-xl shadow-sm">
                <span className="text-sm font-bold text-brand-dark truncate">{uploadedFileName}</span>
                <button onClick={clearFile} className="text-gray-400 hover:text-brand-orange transition-colors">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            ) : (
              <div onClick={() => fileInputRef.current.click()} className="w-full p-10 border-2 border-dashed border-gray-100 rounded-2xl flex flex-col items-center justify-center gap-4 text-gray-400 cursor-pointer hover:border-brand-orange hover:bg-white transition-all group">
                <SafeIcon icon={FiUpload} className="w-10 h-10 group-hover:text-brand-orange transition-colors" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em]">{t.headhunter.upload}</span>
              </div>
            )}
            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".pdf,.docx" />
          </div>

          <textarea 
            value={cvText} 
            onChange={(e) => setCvText(e.target.value)}
            placeholder={t.headhunter.placeholder}
            className="w-full h-48 p-6 border border-gray-50 rounded-2xl resize-none focus:ring-2 focus:ring-brand-orange outline-none text-brand-dark mb-8 text-sm bg-gray-50/30 italic font-medium"
          />

          <button 
            onClick={handleAnalyze}
            disabled={isAnalyzing || (!cvText.trim() && !uploadedFileName)}
            className={`w-full py-6 rounded-2xl font-black text-white transition-all flex items-center justify-center gap-4 shadow-xl uppercase tracking-widest ${isAnalyzing ? 'bg-gray-300' : 'bg-brand-orange hover:bg-orange-600 hover:-translate-y-0.5'}`}
          >
            {isAnalyzing ? (
              <><SafeIcon icon={FiActivity} className="animate-spin" /> {t.headhunter.analyzing}</>
            ) : (
              <>{t.headhunter.cta} <SafeIcon icon={FiArrowRight} className="w-6 h-6" /></>
            )}
          </button>
        </div>

        {showResults && result && (
          <div className="space-y-12 animate-fade-in pb-32">
            <div className="bg-white p-8 rounded-3xl border border-orange-50 shadow-sm flex items-start gap-6">
              <div className="w-14 h-14 bg-brand-orange rounded-2xl flex items-center justify-center flex-shrink-0 text-white font-black italic shadow-lg">V</div>
              <div>
                <p className="text-sm font-black text-brand-dark mb-1 uppercase tracking-tight">{t.headhunter.gretaTitle}</p>
                <p className="text-sm text-gray-500 italic font-medium">{t.headhunter.gretaText}</p>
              </div>
            </div>

            <div className="bg-white rounded-[3rem] border border-gray-100 p-12 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8">
                <div className="bg-green-50 text-green-600 px-4 py-2 rounded-xl border border-green-100 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
                  <SafeIcon icon={FiUserCheck} /> {t.headhunter.domain}
                </div>
              </div>
              <div className="mb-12 pb-12 border-b border-gray-50">
                <h2 className="text-[10px] font-black text-gray-300 uppercase tracking-[0.4em] mb-4">{t.headhunter.domain}</h2>
                <p className="text-4xl font-black text-brand-dark italic uppercase tracking-tighter">{result.domain}</p>
              </div>
              <div>
                <h3 className="text-[10px] font-black text-gray-300 uppercase tracking-[0.4em] mb-6">{t.headhunter.skills}</h3>
                <div className="flex flex-wrap gap-3">
                  {result.skills.map((skill, i) => (
                    <span key={i} className="px-5 py-2.5 bg-gray-50 text-brand-dark border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest italic">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-black text-brand-dark flex items-center gap-4 px-4 italic uppercase tracking-tighter">
                {t.headhunter.opportunities}
              </h2>
              {result.matches.map((job, i) => (
                <div key={i} className="bg-white rounded-[2.5rem] border border-gray-100 p-10 shadow-sm hover:border-brand-orange transition-all group">
                  <h3 className="text-2xl font-black text-brand-dark mb-6 group-hover:text-brand-orange transition-colors italic uppercase tracking-tight">{job.title}</h3>
                  <div className="bg-orange-50/30 p-8 rounded-2xl border-l-4 border-l-brand-orange mb-8">
                    <p className="text-sm text-brand-dark leading-relaxed font-bold italic">
                      "{job.explanation}"
                    </p>
                  </div>
                  <button className="bg-brand-dark text-white px-8 py-4 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-black transition-all shadow-lg">
                    {t.headhunter.viewJob}
                  </button>
                </div>
              ))}
            </div>

            {/* AI DISCLAIMER SECTION */}
            <div className="bg-gray-50 border border-gray-200 p-10 rounded-[2.5rem] shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <SafeIcon icon={FiAlertCircle} className="text-brand-orange w-6 h-6" />
                <h3 className="text-lg font-black text-brand-dark uppercase tracking-widest italic">
                  {t.headhunter.aiDisclaimerTitle}
                </h3>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                {t.headhunter.aiDisclaimerText}
              </p>
            </div>

            <div className="p-8 bg-gray-50/50 rounded-2xl border border-gray-100 flex items-start gap-4">
              <SafeIcon icon={FiInfo} className="text-gray-300 mt-0.5" />
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] leading-relaxed">
                {t.headhunter.disclaimer}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIHeadhunter;